<template>
  <article class="po-card" :class="`is-${item.paymentStatus || 'unknown'}`">
    <!-- ==================== Error dialog + toast (unchanged) ==================== -->
    <base-dialog :show="!!error" title="An error occurred!" @close="handleError">
      <p>{{ error }}</p>
    </base-dialog>
    <base-toast
      v-if="!!baseToast.message"
      :message="baseToast.message"
      :type="baseToast.type"
      @close="handleBaseToast"
    ></base-toast>

    <!-- ==================== Modals (teleported) ==================== -->
    <teleport to="body">
      <transition name="modal-fade">
        <div v-if="showPayOutApproveModal" class="po-modal-backdrop" @click.self="showPayOutApproveModal = false">
          <div class="po-modal-card po-modal-wide">
            <div class="po-modal-head">
              <div>
                <div class="po-modal-eyebrow">Pay-out</div>
                <div class="po-modal-title">Approve pay-out order</div>
                <div class="po-modal-sub">Enter UTR(s) and mode to confirm this payout.</div>
              </div>
              <button type="button" class="po-modal-close" @click="showPayOutApproveModal = false">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                  <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
                </svg>
              </button>
            </div>
            <div class="po-modal-body">
              <pay-out-approve-form
                :key="item.receiptId"
                :item="selectedItem"
                @form-response="formResponse"
              ></pay-out-approve-form>
            </div>
          </div>
        </div>
      </transition>

      <transition name="modal-fade">
        <div v-if="showClientEditModal" class="po-modal-backdrop" @click.self="showClientEditModal = false">
          <div class="po-modal-card">
            <div class="po-modal-head">
              <div>
                <div class="po-modal-eyebrow">Pay-out</div>
                <div class="po-modal-title">Edit payout order</div>
                <div class="po-modal-sub">Update beneficiary details or amount on your order.</div>
              </div>
              <button type="button" class="po-modal-close" @click="showClientEditModal = false">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                  <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
                </svg>
              </button>
            </div>
            <div class="po-modal-body">
              <client-edit-payout-form
                :key="item.receiptId"
                :item="selectedItem"
                @close="showClientEditModal = false"
                @updated="handleClientUpdate"
              ></client-edit-payout-form>
            </div>
          </div>
        </div>
      </transition>

      <transition name="modal-fade">
        <div v-if="showForceCompleteModal" class="po-modal-backdrop" @click.self="showForceCompleteModal = false">
          <div class="po-modal-card">
            <div class="po-modal-head po-modal-head-warn">
              <div>
                <div class="po-modal-eyebrow warn">Override</div>
                <div class="po-modal-title">Force-complete payout</div>
                <div class="po-modal-sub">Close this partially settled payout at the paid amount.</div>
              </div>
              <button type="button" class="po-modal-close" @click="showForceCompleteModal = false">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                  <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
                </svg>
              </button>
            </div>
            <div class="po-modal-body">
              <force-complete-form
                :key="`force-${item.refID}`"
                :orderRow="selectedItem"
                @close="showForceCompleteModal = false"
                @completed="handleForceCompleted"
              ></force-complete-form>
            </div>
          </div>
        </div>
      </transition>

      <!-- Reject modal — used by BOTH branch reject (returns to pool)
           AND vendor reject (marks failed). rejectMode discriminates. -->
      <transition name="modal-fade">
        <div v-if="showRejectModal" class="po-modal-backdrop" @click.self="closeRejectModal">
          <div class="po-modal-card">
            <div class="po-modal-head po-modal-head-danger">
              <div>
                <div class="po-modal-eyebrow danger">
                  {{ rejectMode === 'vendor' ? 'Override · Destructive' : 'Return to pool' }}
                </div>
                <div class="po-modal-title">
                  {{ rejectMode === 'vendor' ? 'Reject this payout?' : 'Reject and return' }}
                </div>
                <div class="po-modal-sub">
                  <template v-if="rejectMode === 'vendor'">
                    Marks the order <strong>failed</strong> and sends a failed callback to the client. Cannot be undone.
                  </template>
                  <template v-else>
                    Order will be returned to the available list. A reason is required.
                  </template>
                </div>
              </div>
              <button type="button" class="po-modal-close" @click="closeRejectModal">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                  <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
                </svg>
              </button>
            </div>
            <div class="po-modal-body">
              <form @submit.prevent="submitReject" class="reject-form">
                <div class="reject-summary">
                  <div class="sum-row">
                    <span class="sum-label">Order</span>
                    <span class="sum-value">{{ item.merchantOrderId || item.refID }}</span>
                  </div>
                  <!-- Same masking rule as the card's amount block: a branch
                       on an OPEN partially-settled order must only ever see
                       the remaining amount, never the original total — even
                       in the reject confirmation. Vendor mode still shows
                       the full amount (they see the whole picture anyway). -->
                  <div class="sum-row">
                    <span class="sum-label">Amount</span>
                    <span class="sum-value">
                      ₹{{ formatAmount(branchHidesOriginal && item.remaining_amount ? item.remaining_amount : item.amount) }}
                    </span>
                  </div>
                  <div class="sum-row">
                    <span class="sum-label">Status</span>
                    <span class="sum-value upper">{{ item.paymentStatus }}</span>
                  </div>
                </div>
                <div class="field">
                  <label>Reason <span class="req">*</span></label>
                  <textarea
                    v-model.trim="rejectReason"
                    rows="3"
                    maxlength="500"
                    class="textarea"
                    placeholder="Why is this order being rejected?"
                    autofocus
                  ></textarea>
                  <div class="field-foot">
                    <span class="field-hint">Stored on the audit row.</span>
                    <span class="char-count">{{ rejectReason.length }}/500</span>
                  </div>
                </div>
                <p v-if="rejectErrorMsg" class="form-alert">{{ rejectErrorMsg }}</p>
                <div class="form-actions">
                  <button
                    type="button"
                    class="btn btn-secondary"
                    @click="closeRejectModal"
                    :disabled="isRejecting"
                  >Cancel</button>
                  <button
                    type="submit"
                    class="btn btn-danger"
                    :disabled="isRejecting || !rejectReason.trim()"
                  >
                    <span v-if="!isRejecting">
                      {{ rejectMode === 'vendor' ? 'Mark failed' : 'Return to pool' }}
                    </span>
                    <span v-else class="btn-loading">
                      <span class="spinner-mini"></span>
                      Rejecting…
                    </span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </transition>
    </teleport>

    <!-- Timeline modal (its own component, kept as-is) -->
    <payout-timeline-modal
      :show="showTimelineModal"
      :refID="timelineRefID"
      @close="showTimelineModal = false"
    ></payout-timeline-modal>

    <!-- ==================== Body ==================== -->
    <div class="po-main">
      <!-- Left: info -->
      <div class="po-info">
        <!-- Identity row -->
        <div class="po-identity">
          <div class="po-receipt">
            <span class="receipt-label">Receipt</span>
            <span class="receipt-value">{{ item.receiptId }}</span>
            <base-copy-button :text="item.receiptId" title="Copy Receipt ID" />
          </div>
          <div class="po-badges">
            <span v-if="isBankTransfer" class="pill pill-info">{{ String(item.mode || '').toUpperCase() }}</span>
            <span v-else-if="isUpiMode" class="pill pill-info">UPI</span>
            <span v-if="item.payout_type === 'instant'" class="pill pill-purple">Instant</span>
          </div>
        </div>

        <!-- Info grid -->
        <div class="po-grid">
          <!-- Left column -->
          <div class="po-col">
            <div v-if="isVendor && item.clientName" class="po-field">
              <div class="field-label">Client</div>
              <div class="field-value">
                <span>{{ item.clientName }}</span>
                <base-copy-button :text="item.clientName" title="Copy Client" />
              </div>
            </div>
            <div class="po-field">
              <div class="field-label">Created</div>
              <div class="field-value">{{ formatDate(item.createdAt) }}</div>
            </div>
            <div v-if="item.paymentStatus === 'approved'" class="po-field">
              <div class="field-label">Approved at</div>
              <div class="field-value">{{ formatDate(item.updatedAt) }}</div>
            </div>
            <div v-if="(isAdmin || isSubAdmin) && getApprovedBy" class="po-field">
              <div class="field-label">Approved by</div>
              <div class="field-value">
                <span>{{ getApprovedBy }}</span>
                <base-copy-button :text="getApprovedBy" title="Copy Approved By" />
              </div>
            </div>
            <template v-if="isBankTransfer && !showPickupButton">
              <div class="po-field">
                <div class="field-label">Account holder</div>
                <div class="field-value">
                  <span>{{ item.customerName }}</span>
                  <base-copy-button :text="item.customerName" title="Copy Customer Name (Account Holder)" />
                </div>
              </div>
              <div class="po-field">
                <div class="field-label">Account no.</div>
                <div class="field-value">
                  <span class="mono">{{ item.accountNumber }}</span>
                  <base-copy-button :text="item.accountNumber" title="Copy Account Number" />
                </div>
              </div>
              <div class="po-field">
                <div class="field-label">IFSC</div>
                <div class="field-value">
                  <span class="mono">{{ item.ifsc }}</span>
                  <base-copy-button :text="item.ifsc" title="Copy IFSC" />
                </div>
              </div>
              <div class="po-field">
                <div class="field-label">Bank</div>
                <div class="field-value">
                  <span>{{ item.bankName }}</span>
                  <base-copy-button :text="item.bankName" title="Copy Bank Name" />
                </div>
              </div>
            </template>
          </div>

          <!-- Right column -->
          <div class="po-col">
            <div class="po-field">
              <div class="field-label">Order ID</div>
              <div class="field-value">
                <span class="mono break">{{ item.merchantOrderId }}</span>
                <base-copy-button :text="item.merchantOrderId" title="Copy Order ID" />
              </div>
            </div>
            <div v-if="isAdmin || isSubAdmin" class="po-field">
              <div class="field-label">Assigned to</div>
              <div class="field-value">
                <span>{{ item.validatorUsername }}</span>
                <base-copy-button :text="item.validatorUsername" title="Copy Assigned To" />
              </div>
            </div>
            <div v-if="utrList.length > 0" class="po-field">
              <div class="field-label">UTR{{ utrList.length > 1 ? 's' : '' }}</div>
              <div class="field-value utr-value">
                <template v-if="utrList.length === 1">
                  <span class="mono">{{ utrList[0] }}</span>
                  <base-copy-button :text="utrList[0]" title="Copy UTR" />
                </template>
                <template v-else>
                  <span
                    v-for="(utr, i) in utrList"
                    :key="utr + i"
                    class="utr-chip"
                  >
                    <span class="mono">{{ utr }}</span>
                    <base-copy-button :text="utr" :title="`Copy UTR #${i + 1}`" />
                  </span>
                  <base-copy-button
                    :text="utrList.join(',')"
                    title="Copy all UTRs (comma-separated)"
                    class="utr-copy-all"
                  />
                </template>
              </div>
            </div>
            <div v-if="isUpiMode && !showPickupButton && item.customerUPIID" class="po-field">
              <div class="field-label">Customer UPI</div>
              <div class="field-value">
                <span class="mono break">{{ item.customerUPIID }}</span>
                <base-copy-button :text="item.customerUPIID" title="Copy Customer UPI" />
              </div>
            </div>
            <div class="po-field">
              <div class="field-label">Method</div>
              <div class="field-value">{{ item.mode || 'N/A' }}</div>
            </div>
            <div v-if="item.created_by" class="po-field">
              <div class="field-label">Creator</div>
              <div class="field-value">
                <span>{{ item.created_by }}</span>
                <base-copy-button :text="item.created_by" title="Copy Order Creator" />
              </div>
            </div>
            <div v-if="item.createdAt" class="po-field">
              <div class="field-label">Total time</div>
              <div class="field-value">
                {{ humanElapsed(totalTimeSeconds) }}
                <span v-if="!isTerminal" class="hint-inline">(so far)</span>
              </div>
            </div>
            <div v-if="item.payout_screenshot && screenshotUrls.length" class="po-field">
              <div class="field-label">Attachment{{ screenshotUrls.length > 1 ? 's' : '' }}</div>
              <div class="field-value shots-value">
                <span v-for="(url, index) in screenshotUrls" :key="index" class="shot-item">
                  <a :href="url" target="_blank" class="link">
                    View {{ screenshotUrls.length > 1 ? `#${index + 1}` : '' }}
                  </a>
                  <base-copy-button :text="url" :title="`Copy Attachment${screenshotUrls.length > 1 ? ' #' + (index + 1) : ''}`" />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right sidebar -->
      <div class="po-side">
        <div class="status-pill" :class="`status-${statusTone}`">{{ item.paymentStatus }}</div>

        <!-- Instant payout state pills -->
        <template v-if="item.payout_type === 'instant' && item.paymentStatus === 'unassigned'">
          <div class="side-note" :class="`status-${statusTone}`">Waiting for E2E</div>
          <div v-if="item.instant_payout_expiry_at" class="side-note" :class="`status-${statusTone}`">
            {{ formattedTimeForInstantWaiting }}
          </div>
          <div v-else class="side-note side-note-muted">Customer not accessed yet</div>
        </template>

        <!-- Amount -->
        <div class="amount-block" v-if="item.is_instant_payout && item.payout_type === 'instant' && item.instant_balance > 0">
          <div class="amount-value">₹{{ formatMoney(item.instant_balance) }}</div>
        </div>
        <div v-else class="amount-block">
          <div v-if="branchHidesOriginal && item.remaining_amount" class="amount-value">
            ₹{{ formatMoney(item.remaining_amount) }}
          </div>
          <div v-else-if="item.amount" class="amount-value">
            ₹{{ formatMoney(item.amount) }}
          </div>
        </div>

        <!-- Multi-branch settlement progress -->
        <div v-if="hasPartialSettlement && !branchHidesOriginal"
             class="partial-panel"
             :class="item.paymentStatus === 'approved' ? 'panel-success' : 'panel-info'">
          <div class="partial-title">
            {{ item.paymentStatus === 'approved' ? 'Partially approved' : 'Open for re-pickup' }}
          </div>
          <div class="partial-line">
            Paid: <strong>₹{{ formatAmount(item.approved_amount) }}</strong> of
            <strong>₹{{ formatAmount(item.amount) }}</strong>
          </div>
          <div class="partial-line dim">
            Remaining: ₹{{ formatAmount(item.remaining_amount) }}
          </div>
        </div>

        <!-- Branch-only re-pickup banner -->
        <div v-if="hasPartialSettlement && branchHidesOriginal"
             class="partial-panel panel-info">
          <div class="partial-title">Partial — pay remaining above</div>
        </div>

        <!-- Branch contribution -->
        <div v-if="isSubAdmin && myContribution > 0" class="partial-panel panel-purple">
          <div class="partial-title">You paid ₹{{ formatAmount(myContribution) }}</div>
        </div>

        <!--
          Reason panels (29072026): clients must see WHY a payout ended the
          way it did. Display is deliberately gated by computed guards —
          orders.rejection_reason is overwritten by every internal branch
          reject while the order is still alive, so rendering it raw would
          leak internal routing reasons onto pending/approved orders:
            · failed  + reason              → vendor rejection (always the
              vendor's own text — vendorRejectPayout overwrites at fail time)
            · approved + 'Force-completed:' → force-complete note (prefix
              stamped by forceCompletePayout discriminates it from stale
              branch-reject leftovers)
          rejected_by (operator identity) is intentionally never rendered.
        -->
        <div v-if="vendorRejectionReason" class="partial-panel panel-danger">
          <div class="partial-title">Why this payout was rejected</div>
          <div class="partial-line">{{ vendorRejectionReason }}</div>
        </div>
        <div v-if="forceCompleteReason" class="partial-panel panel-warn">
          <div class="partial-title">Closed early at the paid amount (force completed)</div>
          <div class="partial-line">{{ forceCompleteReason }}</div>
        </div>

        <!-- Actions -->
        <div class="side-actions" @click.stop>
          <!-- Branch Approve / Reject -->
          <template v-if="isSubAdmin && showApproveRejectBtn">
            <button v-if="can('payout.approve') || can('payout.partial_approve')" type="button" class="side-btn side-btn-primary" @click.stop="approveOrder">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                <path d="M20 6L9 17l-5-5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              Approve
            </button>
            <button v-if="can('payout.reject')" type="button" class="side-btn side-btn-danger-ghost" @click.stop="rejectOrder">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
              </svg>
              Reject
            </button>
          </template>

          <!-- Vendor reject -->
          <button
            v-if="isVendor && showVendorRejectBtn && !hasPartialSettlement && can('payout.vendor_reject')"
            type="button"
            class="side-btn side-btn-danger"
            @click.stop="vendorRejectOrder"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
              <circle cx="12" cy="12" r="10" />
              <path d="M15 9l-6 6M9 9l6 6" stroke-linecap="round" />
            </svg>
            Reject
          </button>

          <!-- Force complete -->
          <button v-if="showForceCompleteBtn && can('payout.force_complete')" type="button" class="side-btn side-btn-warn" @click.stop="openForceComplete">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            Force complete
          </button>

          <!-- Resend success callback (28102026): vendor-level, success order only -->
          <button
            v-if="showResendCallbackBtn"
            type="button"
            class="side-btn side-btn-secondary"
            :disabled="resendBusy"
            @click.stop="resendCallback"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M23 4v6h-6M1 20v-6h6" stroke-linecap="round" stroke-linejoin="round" />
              <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            {{ resendBusy ? 'Sending…' : 'Resend callback' }}
          </button>

          <!-- Timeline -->
          <button v-if="!isSubAdmin && !isClient && !isClientPartner" type="button" class="side-btn side-btn-secondary" @click.stop="openTimeline">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v6l4 2" stroke-linecap="round" />
            </svg>
            Timeline
          </button>

          <!-- Pickup / Re-pick -->
          <button
            v-if="showPickupButton && isSubAdmin && item.paymentStatus === 'unassigned' && can('payout.pickup')"
            type="button"
            class="side-btn"
            :class="myContribution > 0 ? 'side-btn-repick' : 'side-btn-pickup'"
            @click.stop="handlePickup"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
              <path d="M5 12h14M12 5l7 7-7 7" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            {{ myContribution > 0 ? 'Re-pick' : 'Pick up' }}
          </button>

          <button
            v-if="!showPickupButton && isSubAdmin && item.paymentStatus === 'unassigned' && myContribution > 0 && can('payout.pickup')"
            type="button"
            class="side-btn side-btn-repick"
            @click.stop="handlePickup"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
              <path d="M23 4v6h-6M1 20v-6h6" stroke-linecap="round" stroke-linejoin="round" />
              <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            Re-pick
          </button>
        </div>
      </div>
    </div>

    <!-- ==================== Instant batch payouts (unchanged, restyled) ==================== -->
    <div v-if="item.payout_type === 'instant'" class="po-batches">
      <div class="batches-head">
        <button type="button" class="side-btn side-btn-secondary" @click.stop="loadTransactionLogs(item)">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M23 4v6h-6M1 20v-6h6" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          Load transaction logs
        </button>
      </div>
      <div class="batches-scroll">
        <table class="batches-table">
          <thead>
            <tr>
              <th>#Batch order</th>
              <th>Created on</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Transaction ID</th>
              <th>From</th>
              <th>To</th>
              <th>Confirmed</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(batch, index) in batchPayouts" :key="index">
              <td>BSO{{ index + 1 }} · Order #{{ batch.id }}</td>
              <td>{{ formatDate(batch.created_at) }}</td>
              <td>{{ batch.amount }}</td>
              <td>{{ batch.status }}</td>
              <td class="mono">{{ batch.utr_no }}</td>
              <td>{{ batch.payment_from }}</td>
              <td>{{ batch.payment_to }}</td>
              <td>{{ batch.confirmed_by_customer_at == null ? 'NO' : 'YES' }}</td>
            </tr>
            <tr v-if="batchPayouts.length === 0 && touched">
              <td colspan="8" class="batches-empty" @click.stop="loadTransactionLogs(item)">
                No batch orders currently — click to refresh
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </article>
</template>

<script>
import PayOutApproveForm from "./PayOutApproveForm.vue";
import ClientEditPayoutForm from "./ClientEditPayoutForm.vue";
import ForceCompleteForm from "./ForceCompleteForm.vue";
import PayoutTimelineModal from "./PayoutTimelineModal.vue";
import moment from 'moment-timezone';
import http from "../../http-common";

export default {
    name: "pay-out-item",
    emits: ["select-item", "switch-view-mode", "pickup-order", "refresh"],
    props: ["item", "slNo", "selectAll", "clearSelected", "showPickupButton"],
    components: {
        PayOutApproveForm,
        ClientEditPayoutForm,
        ForceCompleteForm,
        PayoutTimelineModal,
    },
    data() {
        return {
            touched: false,
            createdAt: "",
            batchPayouts: [],
            resendBusy: false,
            showPayOutApproveModal: false,
            showClientEditModal: false,
            showForceCompleteModal: false,
            // Timeline modal state — see PayoutTimelineModal.vue. Backend
            // role-filters the response so the button is safe for every
            // role (vendor / branch / client each see their scoped view).
            showTimelineModal: false,
            timelineRefID: null,
            baseToast: {
                type: "",
                message: "",
            },
            error: null,
            selectedItem: null,
            isSelected: false,
            // Reject modal state — shared between branch reject (returns to
            // pool) and vendor reject (marks failed). `rejectMode` discriminates
            // so submitReject dispatches the right store action.
            showRejectModal: false,
            rejectMode: null, // 'branch' | 'vendor'
            rejectReason: "",
            rejectErrorMsg: null,
            isRejecting: false,
            instantTimeLeft: 25 * 60 * 1000, // 25 minutes to match backend logic (15 + 10 extension)
            instantIntervalId: null,
            instantTimeExpired: false,
            serverTimeNow: null,
        };
    },

    created() {
        this.createdAt = this.item.createdAt;
        this.startCountdownForInstant();
    },

    watch: {
        selectAll(newVal) {
            this.isSelected = newVal;
        },
        clearSelected() {
            this.isSelected = false;
        },
    },
    computed: {
        // Terminal states for a payout — used by the "Total Time to
        // Complete" field to decide whether updatedAt is the end-of-life
        // timestamp or just the last touch on a still-open order.
        isTerminal() {
            return ['approved', 'failed'].includes(this.item.paymentStatus);
        },
        // Total seconds from createdAt to (updatedAt if terminal, else now).
        // orders.createdAt / updatedAt are raw IST strings per CLAUDE.md —
        // parse via moment.tz so the value is correct regardless of the
        // Node process TZ.
        totalTimeSeconds() {
            if (!this.item.createdAt) return null;
            const tz = process.env.VUE_APP_TIMEZONE || 'Asia/Kolkata';
            const startMs = moment.tz(this.item.createdAt, tz).valueOf();
            if (!Number.isFinite(startMs)) return null;
            const endMs = this.isTerminal && this.item.updatedAt
                ? moment.tz(this.item.updatedAt, tz).valueOf()
                : Date.now();
            return Math.max(0, Math.round((endMs - startMs) / 1000));
        },
        screenshotUrls() {
            if (!this.item.payout_screenshot) return [];
            try {
                const parsed = JSON.parse(this.item.payout_screenshot);
                return Array.isArray(parsed) ? parsed : [this.item.payout_screenshot];
            } catch (e) {
                return [this.item.payout_screenshot];
            }
        },
        // Operators can record multiple UTRs per payout (up to 7, split across
        // bank transfers). The backend stores them as a comma-separated string
        // in `transactionID`; legacy single-UTR rows are just one value.
        utrList() {
            const raw = this.item.transactionID;
            if (!raw) return [];
            return String(raw)
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean);
        },
        formattedTimeForInstantWaiting() {
            const minutes = Math.floor((this.instantTimeLeft / 1000) / 60);
            const seconds = Math.floor((this.instantTimeLeft / 1000) % 60);
            return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
        },
        // Vendor can terminate a payout in any of these still-open states.
        // Once it's approved or already failed, the action is hidden.
        showVendorRejectBtn() {
            const rejectableStates = ['pending', 'unassigned', 'expired'];
            return rejectableStates.includes(this.item.paymentStatus);
        },
        showApproveRejectBtn() {
            // Branches (subadmin) get the same expired-order approval access
            // that vendors (admin) had — they can clean up expired payouts the
            // same way they clean up pending ones.
            const canApproveExpired = this.isAdmin || this.isSubAdmin;
            if (this.item.payout_type == 'instant') {
                return (this.item.paymentStatus == 'pending' || (this.item.paymentStatus == 'expired' && canApproveExpired)) && this.item.instant_balance;
            } else {
                if (this.item.isPayoutLink) {
                    return (this.item.paymentStatus == 'pending' || (this.item.paymentStatus == 'expired' && canApproveExpired)) && this.item.amount && this.item.isApprovedByMOU;
                } else {
                    return (this.item.paymentStatus == 'pending' || (this.item.paymentStatus == 'expired' && canApproveExpired)) && this.item.amount;
                }
            }
        },
        // Status tone drives the left accent bar + pill gradient. Kept in
        // sync with the old getClass computed.
        statusTone() {
            switch (this.item.paymentStatus) {
                case "pending":    return "pending";
                case "approved":   return "success";
                case "rejected":
                case "failed":     return "warn";
                case "unassigned": return "neutral";
                case "expired":    return "danger";
                default:           return "neutral";
            }
        },
        // Kept for template backward-compat callers using the old class name.
        getClass() {
            if (this.item.paymentStatus == "pending") return "bg-blue-500";
            if (this.item.paymentStatus == "approved") return "bg-green-600";
            if (this.item.paymentStatus == "rejected" || this.item.paymentStatus == "failed") return "bg-yellow-600";
            if (this.item.paymentStatus == "unassigned") return "bg-gray-600";
            if (this.item.paymentStatus == "expired") return "bg-red-600";
            return "bg-black";
        },
        isAdmin() {
            return !!this.$store.getters.isAdmin;
        },
        isSubAdmin() {
            return !!this.$store.getters.isSubAdmin;
        },
        // Vendor-level operators (vendor/admin/superadmin). Used to show the
        // IndiaPayU client name on each card so a vendor can tell which of
        // their clients an order belongs to.
        isVendor() {
            const role = this.$store.getters.role;
            return ['vendor', 'admin', 'superadmin'].includes(role);
        },
        // Re-fire the success callback (28102026). Vendor-level only; only for a
        // successful payout (approved/success). Backend replays the stored
        // success payload and logs a new callback_logs entry.
        showResendCallbackBtn() {
            return this.isVendor && ['approved', 'success'].includes(this.item.paymentStatus);
        },
        isUser() {
            return !!this.$store.getters.isUser;
        },
        isClient() {
            return !!this.$store.getters.isClient;
        },
        // Client Partner (11102026) — view-only; mirrors the CLIENT's surface.
        isClientPartner() {
            return !!this.$store.getters.isClientPartner;
        },
        isUpiMode() {
            return String(this.item.mode || '').toLowerCase() === 'upi';
        },
        isBankTransfer() {
            const mode = String(this.item.mode || '').toUpperCase();
            return ['IMPS', 'NEFT', 'RTGS'].includes(mode);
        },
        getApprovedBy() {
            if (this.item.approvedBy == "user") {
                return `${this.item.validatorUsername}(Bank)`;
            } else if (this.item.approvedBy == "subadmin") {
                // MULTIPART signals the order was closed across multiple
                // branches. Surface that explicitly so reports / read-paths
                // don't try to interpret it as a real username.
                if (this.item.approvedByUsername === 'MULTIPART') {
                    return 'Multi-branch settlement';
                }
                return `${this.item.approvedByUsername
                    ? this.item.approvedByUsername
                    : this.item.validatorUsername
                    }(Branch)`;
            } else if (this.item.approvedBy == "admin") {
                return "Admin";
            } else {
                return "";
            }
        },
        // True when the order has had at least one manual settlement —
        // covers both legacy one-time-partial (status='approved' with
        // remaining > 0) and mid-flight re-pickable (status='unassigned'
        // with approved_amount > 0).
        hasPartialSettlement() {
            const approved = parseFloat(this.item.approved_amount || 0);
            const remaining = parseFloat(this.item.remaining_amount || 0);
            return approved > 0 && remaining > 0;
        },
        // Branches should NEVER see the original total on a still-open
        // partial — they were paying the full amount instead of just what
        // remained. Covers the whole open lifecycle for a branch:
        //  - 'unassigned'  (in Available list, about to Pick Up)
        //  - 'pending'     (already picked up, in My Orders / approve form)
        //  - 'expired'     (branch can still approve via expired path)
        // Closed terminal states ('approved' / 'rejected' / etc.) stay
        // informational with the full breakdown — no payment risk there.
        // All non-subadmin roles always keep the current display.
        branchHidesOriginal() {
            const openStates = ['unassigned', 'pending', 'expired'];
            return this.isSubAdmin
                && this.hasPartialSettlement
                && openStates.includes(this.item.paymentStatus);
        },
        // Per-branch contribution to this order. Set by getOrders for
        // subadmin viewers only — sum of THIS branch's manual settlements
        // on this order. Drives the "You paid ₹X" inline stat + the
        // Re-Pick button styling on orders the branch previously settled
        // a portion of but no longer holds.
        myContribution() {
            return parseFloat(this.item.my_settled_amount || 0) || 0;
        },
        // Vendor-rejection reason — safe to surface ONLY on failed payouts:
        // 'failed' has exactly one writer (vendorRejectPayout) and it
        // overwrites rejection_reason with the vendor's own text at fail
        // time, so a stale internal branch-reject reason can never show.
        vendorRejectionReason() {
            if (this.item.type !== 'payout') return '';
            if (this.item.paymentStatus !== 'failed') return '';
            return String(this.item.rejection_reason || '').trim();
        },
        // Force-complete note — forceCompletePayout stamps the reason with
        // a 'Force-completed:' prefix; that prefix is the discriminator
        // that keeps stale branch-reject reasons off approved orders.
        forceCompleteReason() {
            if (this.item.type !== 'payout') return '';
            if (this.item.paymentStatus !== 'approved') return '';
            const r = String(this.item.rejection_reason || '').trim();
            if (!r.startsWith('Force-completed:')) return '';
            return r.replace(/^Force-completed:\s*/, '');
        },
        // Force-complete is only meaningful on partial-settled, still-open
        // orders. Backend additionally enforces role + vendor scope.
        showForceCompleteBtn() {
            if (!this.hasPartialSettlement) return false;
            const stillOpen = ['unassigned', 'pending', 'expired'].includes(this.item.paymentStatus);
            if (!stillOpen) return false;
            const role = this.$store.getters.role;
            return ['admin', 'superadmin', 'client'].includes(role);
        },
    },
    methods: {
        // Permission gate for action buttons. Main users pass (hasPermission
        // returns true); supporting users only see actions they hold.
        can(perm) {
          return this.$store.getters.hasPermission(perm);
        },

        async loadTransactionLogs(itemData) {
            let url = `/orders/${itemData.refID}/batch-payout-details`;
            try {
                const response = await http.get(url);
                if (response.status == 200) {
                    this.touched = true;

                    try {
                        if (response.data.data.batchItems.length > 0) {
                            let batches = response.data.data.batchItems;

                            this.batchPayouts = batches;
                            console.log(batches);
                        }
                    } catch (e) {
                        console.log("sdsddd");
                    }

                    console.log(response.data);
                }

            } catch (error) {
                //console.log(error);
            }



        },


        // from gpt

        startCountdownForInstant() {
            // Only show timer for instant payout orders that are unassigned AND have backend timer set
            if (this.item.payout_type !== 'instant' ||
                this.item.paymentStatus !== 'unassigned' ||
                !this.item.instant_payout_expiry_at) {
                console.log("Skipping timer - conditions not met:", {
                    payout_type: this.item.payout_type,
                    paymentStatus: this.item.paymentStatus,
                    instant_payout_expiry_at: this.item.instant_payout_expiry_at
                });
                return;
            }

            // Get the time zone from the environment variable (e.g., 'Asia/Kolkata')
            const timeZone = process.env.VUE_APP_TIMEZONE;

            // Use the backend timer instead of calculating from createdAt
            const backendExpiryTime = moment.tz(this.item.instant_payout_expiry_at, timeZone).valueOf();

            console.log("Using backend timer expiry:", moment.tz(this.item.instant_payout_expiry_at, timeZone).format("YYYY-MM-DD HH:mm:ss"));

            // Start the countdown by setting an interval
            this.instantIntervalId = setInterval(() => {
                // Get the current time in the same time zone (Asia/Kolkata)
                const now = moment.tz(new Date(), timeZone).valueOf();

                // Calculate the remaining time using backend expiry
                const timeLeft = Math.max(backendExpiryTime - now, 0);

                // Update instantTimeLeft
                this.instantTimeLeft = timeLeft;

                // Check if the countdown expired
                if (this.instantTimeLeft === 0) {
                    this.instantTimeExpired = true; // Countdown expired
                    clearInterval(this.instantIntervalId); // Clear the interval
                    console.log("Admin dashboard timer expired for order:", this.item.refID);
                }
            }, 1000); // Update every second
        },

        formatDate(inputDate) {
            if (!inputDate) return 'N/A';

            const x = inputDate.replace("T", " ");
            const y = x.replace(".000Z", "");

            const date = new Date(y);

            const day = date.getDate();
            const month = date.getMonth() + 1;
            const year = date.getFullYear();
            const hours = date.getHours();
            const minutes = date.getMinutes();
            const seconds = date.getSeconds();
            const ampm = hours >= 12 ? "pm" : "am";

            const formattedDate = `${day.toString().padStart(2, '0')}-${month.toString().padStart(2, '0')}-${year} ${hours %
                12}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')} ${ampm}`;

            return formattedDate;
        },
        formatMoney(v) {
            const n = Number(v) || 0;
            return n.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
        },
        // Human-readable duration for the "Total Time to Complete" card
        // field. Same format used by PayoutTimelineModal.humanElapsed so
        // whatever the vendor sees inside the timeline matches what
        // renders on the card row.
        humanElapsed(seconds) {
            if (seconds == null) return '—';
            const s = Math.round(Number(seconds) || 0);
            if (s < 60) return `${s}s`;
            const m = Math.floor(s / 60);
            const rem = s % 60;
            if (m < 60) return rem ? `${m}m ${rem}s` : `${m}m`;
            const h = Math.floor(m / 60);
            const mm = m % 60;
            if (h < 24) return mm ? `${h}h ${mm}m` : `${h}h`;
            const d = Math.floor(h / 24);
            const hh = h % 24;
            return hh ? `${d}d ${hh}h` : `${d}d`;
        },
        // Shallow snapshot of the row's `item` at modal-open time. Modals
        // bind to `selectedItem` (NOT the live `item` prop) so a socket
        // event in the background — which mutates the order row in the
        // Vuex store via `payout/mergeOrderRow` — can't leak new field
        // values into the open modal mid-edit. Re-cloned on every open
        // so closing + reopening picks up the latest committed values.
        snapshotItem() {
            return { ...this.item };
        },
        approveOrder() {
            this.selectedItem = this.snapshotItem();
            this.showPayOutApproveModal = true;
        },
        openTimeline() {
            // Store the refID separately from selectedItem so the
            // timeline modal can key its watcher off a stable id even
            // if the parent list re-renders around it.
            this.timelineRefID = this.item.refID;
            this.showTimelineModal = true;
        },
        openForceComplete() {
            this.selectedItem = this.snapshotItem();
            this.showForceCompleteModal = true;
        },
        handleForceCompleted(message) {
            this.showForceCompleteModal = false;
            this.baseToast.type = 'success';
            this.baseToast.message = message;
            setTimeout(() => {
                this.handleBaseToast();
                this.$emit('switch-view-mode');
            }, 800);
        },
        formatAmount(n) {
            const v = parseFloat(n || 0);
            return Number.isFinite(v) ? v.toFixed(2) : '0.00';
        },
        vendorRejectOrder() {
            // Vendor reject TERMINATES the payout: paymentStatus -> 'failed',
            // and the merchant gets a failed callback. Distinct from the
            // branch reject below which returns the order to 'unassigned'.
            this.openRejectModal('vendor');
        },
        rejectOrder() {
            // Branch reject sends the order BACK to 'unassigned' so another
            // branch can pick it up. A reason is required for audit.
            this.openRejectModal('branch');
        },
        openRejectModal(mode) {
            this.rejectMode = mode;
            this.rejectReason = "";
            this.rejectErrorMsg = null;
            this.isRejecting = false;
            this.showRejectModal = true;
        },
        closeRejectModal() {
            if (this.isRejecting) return;
            this.showRejectModal = false;
            this.rejectMode = null;
            this.rejectReason = "";
            this.rejectErrorMsg = null;
        },
        async submitReject() {
            if (!this.rejectReason || !this.rejectReason.trim()) {
                this.rejectErrorMsg = "A rejection reason is required.";
                return;
            }
            this.isRejecting = true;
            this.rejectErrorMsg = null;
            const action = this.rejectMode === 'vendor'
                ? 'payin/vendorRejectPayoutOrder'
                : 'payin/rejectPayoutOrder';
            const successMsg = this.rejectMode === 'vendor'
                ? `Pay Out Order ${this.item.merchantOrderId} has been marked failed.`
                : `Pay Out Order ${this.item.merchantOrderId} has been returned to the available list.`;
            try {
                await this.$store.dispatch(action, {
                    refID: this.item.refID,
                    rejection_reason: this.rejectReason.trim(),
                });
                this.showRejectModal = false;
                this.rejectMode = null;
                this.rejectReason = "";
                this.baseToast = { type: 'success', message: successMsg };
                setTimeout(() => {
                    this.handleBaseToast();
                    this.$emit('switch-view-mode');
                }, 800);
            } catch (error) {
                this.rejectErrorMsg = error.message || "Failed to reject the order.";
            } finally {
                this.isRejecting = false;
            }
        },
        handleError() {
            this.error = null;
        },
        async resendCallback() {
            if (this.resendBusy) return;
            this.resendBusy = true;
            try {
                const res = await http.post(`/orders/${this.item.refID}/resend-callback`);
                this.baseToast = { type: 'success', message: (res.data && res.data.message) || 'Success callback re-fired.' };
                setTimeout(() => this.handleBaseToast(), 1200);
            } catch (error) {
                this.baseToast = { type: 'error', message: (error.response && error.response.data && error.response.data.message) || 'Could not resend the callback.' };
                setTimeout(() => this.handleBaseToast(), 1800);
            } finally {
                this.resendBusy = false;
            }
        },
        handleBaseToast() {
            this.baseToast = {
                type: "",
                message: "",
            };
        },
        formResponse(status, message) {
            this.showPayOutApproveModal = false;
            if (status == "success") {
                this.baseToast.type = status;
                this.baseToast.message = message;
                let $this = this;
                setTimeout(() => {
                    $this.handleBaseToast();
                }, 1000);
                // Switch to available orders view then back to my orders to trigger refresh
                setTimeout(() => {
                    $this.$emit('switch-view-mode');
                }, 500);
            } else {
                this.error = message;
            }
        },
        selectItem() {
            this.$emit('select-item', this.item.refID, this.isSelected);
        },
        handlePickup() {
            // Branch self-assigns; no bank selection needed.
            this.$emit('pickup-order', this.item.refID, null);
        },
        editOrder() {
            this.selectedItem = this.snapshotItem();
            this.showClientEditModal = true;
        },
        handleClientUpdate(message) {
            this.showClientEditModal = false;
            this.baseToast.type = 'success';
            this.baseToast.message = message;
            setTimeout(() => {
                this.handleBaseToast();
                // Emit refresh event to parent
                this.$emit('refresh');
            }, 1000);
        },
    },
};
</script>

<style scoped>
/* ==================== Card ==================== */
.po-card {
  background: #fff;
  border: 1px solid #ede9df;
  border-radius: 12px;
  transition: box-shadow 0.15s, transform 0.15s, border-color 0.15s;
  position: relative;
  display: flex;
  flex-direction: column;
}
.po-card::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: #cbd5e1;
  border-radius: 12px 0 0 12px;
  transition: background 0.15s;
}
.po-card.is-pending::before    { background: linear-gradient(180deg, #f59e0b, #d97706); }
.po-card.is-approved::before   { background: linear-gradient(180deg, #10b981, #059669); }
.po-card.is-rejected::before   { background: linear-gradient(180deg, #f59e0b, #b45309); }
.po-card.is-failed::before     { background: linear-gradient(180deg, #f59e0b, #b45309); }
.po-card.is-expired::before    { background: linear-gradient(180deg, #ef4444, #dc2626); }
.po-card.is-unassigned::before { background: linear-gradient(180deg, #94a3b8, #64748b); }

.po-card:hover {
  border-color: #d9d3c1;
  box-shadow: 0 6px 16px -10px rgba(27, 42, 71, 0.14);
}

.po-main {
  display: grid;
  grid-template-columns: 1fr 220px;
}

/* ==================== Info side ==================== */
.po-info {
  padding: 12px 14px 12px 16px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.po-identity {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  padding-bottom: 6px;
  border-bottom: 1px solid #f4f0e5;
}
.po-receipt {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap;
}
.receipt-label {
  font-size: 10px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #94a3b8;
  font-weight: 600;
}
.receipt-value {
  font-family: "SFMono-Regular", Menlo, monospace;
  font-size: 12.5px;
  font-weight: 700;
  color: #1b2a47;
  letter-spacing: 0.02em;
}

.po-badges {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}
.pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  white-space: nowrap;
}
.pill-info   { background: #eef2f7; color: #1B2A47; border: 1px solid #cbd5e1; }
.pill-purple { background: #f3e8ff; color: #6b21a8; border: 1px solid #d8b4fe; }

/* ---------- Info grid ---------- */
.po-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 20px;
  row-gap: 4px;
}
.po-col {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.po-field {
  display: grid;
  grid-template-columns: 96px 1fr;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
  padding: 1px 0;
}
.field-label {
  font-size: 10.5px;
  letter-spacing: 0.04em;
  color: #94a3b8;
  font-weight: 500;
  line-height: 1.4;
}
.field-value {
  font-size: 12.5px;
  color: #1b2a47;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  flex-wrap: wrap;
  line-height: 1.4;
}
.field-value > span,
.field-value > a {
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
/*
  `.field-value .mono` used to swap machine identifiers (Order ID / UPI /
  UTR / account number / IFSC) to a monospace face at 11.5px. Dropped so
  every value in a payout card reads with the same font + size as name-
  style fields — consistency won over identifier legibility. Dead `.mono`
  class refs on <span>s are silently no-op. The `.break` rule below is
  unrelated and still needed for long IDs that would otherwise overflow.
*/
.field-value .break { word-break: break-all; overflow-wrap: anywhere; }
.hint-inline { font-size: 10.5px; color: #94a3b8; margin-left: 4px; }

.link {
  color: #1B2A47;
  font-weight: 600;
  text-decoration: none;
  transition: color 0.15s;
  font-size: 12px;
}
.link:hover { color: #0f172a; text-decoration: underline; }

/* UTR chips (multi-UTR) */
.utr-value {
  align-items: center;
}
.utr-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #eef2f7;
  color: #1B2A47;
  padding: 2px 6px;
  border-radius: 5px;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid #cbd5e1;
}
.utr-copy-all { margin-left: 2px; }

/* Screenshot list */
.shots-value { gap: 8px; }
.shot-item { display: inline-flex; align-items: center; gap: 3px; }

/* ==================== Side ==================== */
.po-side {
  background: #fbfaf7;
  border-left: 1px solid #f1ede2;
  border-radius: 0 12px 12px 0;
  padding: 12px 12px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  gap: 6px;
  min-width: 0;
}

.status-pill {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #fff;
  border: 1px solid transparent;
  align-self: center;
  white-space: nowrap;
}
.status-pending    { background: linear-gradient(135deg, #f59e0b, #d97706); }
.status-success    { background: linear-gradient(135deg, #10b981, #059669); }
.status-warn       { background: linear-gradient(135deg, #f59e0b, #b45309); }
.status-danger     { background: linear-gradient(135deg, #ef4444, #dc2626); }
.status-neutral    { background: linear-gradient(135deg, #94a3b8, #64748b); }

.side-note {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #fff;
  align-self: center;
  white-space: nowrap;
}
.side-note-muted {
  background: #e2e8f0;
  color: #475569;
}

.amount-block {
  text-align: center;
  margin-top: 2px;
}
.amount-value {
  font-size: 19px;
  font-weight: 800;
  color: #1b2a47;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
  line-height: 1.1;
}

/* Partial settlement panels */
.partial-panel {
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid;
  font-size: 11px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.partial-title { font-weight: 700; font-size: 11px; }
.partial-line { font-size: 11px; }
.partial-line strong { font-weight: 700; font-variant-numeric: tabular-nums; }
.partial-line.dim { color: #64748b; }

.panel-success { background: #d1fae5; border-color: #a7f3d0; color: #047857; }
.panel-success .partial-line { color: #047857; }
.panel-info { background: #dbeafe; border-color: #bfdbfe; color: #1e40af; }
.panel-info .partial-line { color: #1e40af; }
.panel-purple { background: #f3e8ff; border-color: #d8b4fe; color: #6b21a8; }
/* Reason panels — rejection (danger) + force-complete note (warn). Long
   reasons wrap; the free-text line drops the bold weight of the title. */
.panel-danger { background: #fef2f2; border-color: #fecaca; color: #b91c1c; }
.panel-danger .partial-line { color: #991b1b; font-weight: 500; word-break: break-word; }
.panel-warn { background: #fef3c7; border-color: #fde68a; color: #b45309; }
.panel-warn .partial-line { color: #92400e; font-weight: 500; word-break: break-word; }

/* ---------- Actions ---------- */
.side-actions {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
  align-items: stretch;
}

.side-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.15s;
  font-family: inherit;
}
.side-btn svg { width: 13px; height: 13px; }
.side-btn:disabled { opacity: 0.55; cursor: not-allowed; }

.side-btn-primary {
  background: linear-gradient(135deg, #10b981, #059669);
  color: #fff;
  box-shadow: 0 4px 12px -6px rgba(16, 185, 129, 0.5);
}
.side-btn-primary:hover:not(:disabled) { transform: translateY(-1px); }

.side-btn-secondary {
  background: #fff;
  color: #1B2A47;
  border-color: #cbd5e1;
}
.side-btn-secondary:hover:not(:disabled) { background: #eef2f7; border-color: #94a3b8; }

.side-btn-danger {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: #fff;
  box-shadow: 0 4px 12px -6px rgba(220, 38, 38, 0.5);
}
.side-btn-danger:hover:not(:disabled) { transform: translateY(-1px); }

.side-btn-danger-ghost {
  background: #fef2f2;
  color: #b91c1c;
  border-color: #fecaca;
}
.side-btn-danger-ghost:hover:not(:disabled) { background: #fee2e2; color: #991b1b; }

.side-btn-warn {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: #fff;
  box-shadow: 0 4px 12px -6px rgba(217, 119, 6, 0.5);
}
.side-btn-warn:hover:not(:disabled) { transform: translateY(-1px); }

.side-btn-pickup {
  background: linear-gradient(135deg, #1B2A47, #2C3E63);
  color: #fff;
  box-shadow: 0 4px 12px -6px rgba(27, 42, 71, 0.5);
}
.side-btn-pickup:hover:not(:disabled) { transform: translateY(-1px); }

.side-btn-repick {
  background: linear-gradient(135deg, #6b21a8, #7e22ce);
  color: #fff;
  box-shadow: 0 4px 12px -6px rgba(126, 34, 206, 0.5);
}
.side-btn-repick:hover:not(:disabled) { transform: translateY(-1px); }

/* ==================== Batches (instant payout) ==================== */
.po-batches {
  padding: 14px 16px;
  border-top: 1px solid #f4f0e5;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.batches-head { display: flex; }
.batches-scroll {
  background: #fbfaf7;
  border: 1px solid #ede9df;
  border-radius: 10px;
  overflow-x: auto;
}
.batches-table {
  min-width: 100%;
  font-size: 12px;
  border-collapse: collapse;
}
.batches-table th {
  padding: 8px 10px;
  text-align: left;
  color: #64748b;
  font-weight: 700;
  font-size: 10.5px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  background: #f4f0e5;
  border-bottom: 1px solid #ede9df;
  white-space: nowrap;
}
.batches-table td {
  padding: 8px 10px;
  color: #334155;
  border-bottom: 1px solid #f4f0e5;
  white-space: nowrap;
}
.batches-table td.mono {
  font-family: "SFMono-Regular", Menlo, monospace;
  font-size: 11.5px;
}
.batches-table tr:last-child td { border-bottom: none; }
.batches-empty {
  text-align: center !important;
  color: #64748b !important;
  cursor: pointer;
  font-style: italic;
}

/* ==================== Responsive ==================== */
@media (max-width: 900px) {
  .po-main {
    grid-template-columns: 1fr;
  }
  .po-side {
    border-left: none;
    border-top: 1px solid #f1ede2;
    border-radius: 0 0 12px 12px;
    padding: 10px 12px;
  }
  .amount-block { margin-top: 0; }
}
@media (max-width: 640px) {
  .po-grid { grid-template-columns: 1fr; }
  .po-info { padding: 10px 12px 10px 14px; }
  .po-field { grid-template-columns: 80px 1fr; }
}

/* ==================== Modals (teleported to body) ==================== */
.po-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.po-modal-card {
  background: #fff;
  border-radius: 18px;
  width: 100%;
  max-width: 520px;
  max-height: calc(100vh - 40px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 60px -20px rgba(15, 23, 42, 0.35);
  border: 1px solid #ede9df;
}
.po-modal-wide { max-width: 780px; }

.po-modal-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 22px 14px;
  border-bottom: 1px solid #f1ede2;
}
.po-modal-head-warn { background: linear-gradient(180deg, #fefce8 0%, #fff 100%); border-bottom-color: #fde68a; }
.po-modal-head-danger { background: linear-gradient(180deg, #fef2f2 0%, #fff 100%); border-bottom-color: #fecaca; }

.po-modal-eyebrow {
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #8a8371;
  font-weight: 700;
}
.po-modal-eyebrow.warn { color: #b45309; }
.po-modal-eyebrow.danger { color: #b91c1c; }

.po-modal-title {
  font-size: 18px;
  font-weight: 700;
  color: #1b2a47;
  margin: 2px 0 4px;
  letter-spacing: -0.01em;
}
.po-modal-sub { font-size: 12.5px; color: #64748b; line-height: 1.4; }

.po-modal-close {
  border: none;
  background: transparent;
  cursor: pointer;
  color: #94a3b8;
  width: 30px; height: 30px;
  border-radius: 8px;
  display: inline-flex; align-items: center; justify-content: center;
  transition: all 0.12s;
  flex-shrink: 0;
}
.po-modal-close:hover { background: #f1f5f9; color: #1b2a47; }
.po-modal-close svg { width: 16px; height: 16px; }

.po-modal-body {
  padding: 18px 22px 22px;
  overflow-y: auto;
  max-height: calc(100vh - 200px);
}

.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.22s ease; }
.modal-fade-enter-from, .modal-fade-leave-to { opacity: 0; }
.modal-fade-enter-active .po-modal-card, .modal-fade-leave-active .po-modal-card {
  transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}
.modal-fade-enter-from .po-modal-card { transform: translateY(-8px) scale(0.98); }

/* ==================== Reject modal body ==================== */
.reject-form { display: flex; flex-direction: column; gap: 14px; }

.reject-summary {
  background: #fbfaf7;
  border: 1px solid #ede9df;
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.sum-row {
  display: grid;
  grid-template-columns: 80px 1fr;
  gap: 8px;
  align-items: baseline;
  font-size: 12.5px;
}
.sum-label { color: #94a3b8; font-weight: 500; }
.sum-value { color: #1b2a47; font-weight: 600; font-variant-numeric: tabular-nums; }
.sum-value.upper { text-transform: uppercase; letter-spacing: 0.03em; }

.field { display: flex; flex-direction: column; }
.field label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 6px;
}
.req { color: #dc2626; }
.textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  background: #fbfaf7;
  color: #0f172a;
  font-family: inherit;
  outline: none;
  resize: vertical;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}
.textarea:focus {
  border-color: #dc2626;
  background: #fff;
  box-shadow: 0 0 0 4px rgba(220, 38, 38, 0.10);
}
.field-foot {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-top: 6px;
  font-size: 11px;
}
.field-hint { color: #94a3b8; }
.char-count { color: #94a3b8; font-variant-numeric: tabular-nums; }

.form-alert {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12.5px;
  margin: 0;
  font-weight: 500;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 4px;
}
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s;
  outline: none;
  font-family: inherit;
}
.btn:disabled { opacity: 0.55; cursor: not-allowed; }
.btn-secondary {
  background: #fff;
  color: #475569;
  border-color: #e2e8f0;
}
.btn-secondary:hover:not(:disabled) { background: #fbfaf7; border-color: #cbd5e1; color: #1b2a47; }
.btn-danger {
  background: linear-gradient(135deg, #dc2626, #b91c1c);
  color: #fff;
  box-shadow: 0 6px 14px -6px rgba(220, 38, 38, 0.45);
}
.btn-danger:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 20px -8px rgba(220, 38, 38, 0.55);
}
.btn-loading { display: inline-flex; align-items: center; gap: 8px; }
.spinner-mini {
  width: 12px; height: 12px;
  border-radius: 999px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  animation: spin 0.7s linear infinite;
  display: inline-block;
}
</style>
